import React from 'react';
import { Lightbulb } from 'lucide-react';
import KPICard from '../../components/shared/KPICard';
import AlarmDot from '../../components/shared/AlarmDot';
import StatusPill from '../../components/shared/StatusPill';
import SectionDivider from '../../components/shared/SectionDivider';
import DonutChart from '../../components/charts/DonutChart';
import { nationalAlarms, faultDistribution, pgvclCircles } from '../../data/mockData';
import { AlarmSeverity } from '../../types';
import { relativeTime } from '../../utils/dateHelpers';

export default function DiscomAlarms() {
  return (
    <div className="p-6 space-y-6">
      <div className="flex gap-4">
        <KPICard title="Active Alarms" value="58" bgColor="bg-red-50" subtextColor="red" subtext="PGVCL wide" />
        <KPICard title="Critical" value="8" bgColor="bg-red-50" subtextColor="red" subtext="Feeder-level" />
        <KPICard title="Warning" value="31" bgColor="bg-orange-50" subtextColor="amber" subtext="Plant-level" />
        <KPICard title="Resolved Today" value="14" subtextColor="green" subtext="Avg MTTR: 4.2h" />
      </div>

      <div className="flex gap-4">
        <div className="flex-1 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="Alarm Feed — PGVCL" subtitle="Recent active alarms" />
          <div className="space-y-3">
            {nationalAlarms.slice(0, 5).map(alarm => (
              <div key={alarm.id} className={`flex gap-3 p-3 rounded-input border ${alarm.status === 'RESOLVED' ? 'border-border/40 opacity-70' : 'border-border'}`}>
                <AlarmDot severity={alarm.severity} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-semibold text-text-primary leading-tight">{alarm.description}</p>
                    <StatusPill
                      status={alarm.severity === AlarmSeverity.CRITICAL ? 'critical' : alarm.severity === AlarmSeverity.WARNING ? 'warning' : 'info'}
                    />
                  </div>
                  <p className="text-xs text-text-tertiary mt-1">
                    <span className="font-mono">{alarm.plantId}</span> · {relativeTime(alarm.timestamp)} · {alarm.duration}
                  </p>
                  {alarm.status === 'ACTIVE' && (
                    <button className="mt-2 flex items-center gap-1 text-xs text-accent hover:underline">
                      <Lightbulb size={11} /> Get advice
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-4 w-72 flex-shrink-0">
          <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
            <SectionDivider title="Fault Distribution" />
            <DonutChart data={faultDistribution} height={180} />
          </div>

          <div className="bg-white rounded-card border border-border shadow-card p-4">
            <SectionDivider title="MTTR by Circle" />
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-1.5 text-text-tertiary font-medium">Circle</th>
                  <th className="text-right py-1.5 text-text-tertiary font-medium">Alarms</th>
                  <th className="text-right py-1.5 text-text-tertiary font-medium">MTTR (h)</th>
                </tr>
              </thead>
              <tbody>
                {pgvclCircles.map(c => (
                  <tr key={c.id} className="border-b border-border/50">
                    <td className="py-1.5 text-text-secondary truncate max-w-0" style={{ maxWidth: 110 }}>{c.name.replace(' Circle', '')}</td>
                    <td className="py-1.5 text-right text-text-secondary">{c.alarms}</td>
                    <td className={`py-1.5 text-right font-semibold ${c.mttrHours > 4.5 ? 'text-warning' : 'text-success'}`}>{c.mttrHours}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
